/**
 * RecordReturnPill — subtle floating "\u2190 Deck" chip that appears on
 * every page while the Battle On recording deck is "active".
 *
 * Flow:
 *   1. Owen taps a scene button on /record — that button sets
 *      sessionStorage.battleon_record_active = "1".
 *   2. Every page in the app renders this pill (via App.js).
 *   3. The pill only shows itself when the flag is set AND we are NOT
 *      currently on /record itself.
 *   4. Tap it \u2192 clears the flag and navigates to /record.
 *
 * Kept intentionally small and gold-on-dark so it reads as "app chrome"
 * during the screen recording, not as marketing UI.
 *
 * Owen Jul 2026.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const RECORD_FLAG_KEY = 'battleon_record_active';

const RecordReturnPill = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);

  useEffect(() => {
    try {
      setActive(sessionStorage.getItem(RECORD_FLAG_KEY) === '1');
    } catch { setActive(false); }
  }, [pathname]);

  if (!active || pathname === '/record') return null;

  const handleReturn = () => {
    // Keep the flag set — Owen may want to tap another scene from the deck.
    navigate('/record');
  };

  return (
    <button
      type="button"
      onClick={handleReturn}
      data-testid="record-return-pill"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[130] inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/70 bg-[#1F1B19]/95 backdrop-blur-sm text-[#D4AF37] hover:text-[#E8C5A0] hover:border-[#D4AF37] shadow-[0_10px_30px_rgba(0,0,0,0.55)] transition-all duration-300 active:scale-[0.97]"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="text-sm italic">Return to Deck</span>
    </button>
  );
};

export default RecordReturnPill;
